/**
 * DecisionLatencyReportPage — L2 of the Decision Latency Instrumentation PRD.
 *
 * Route: /prioritization/decision-latency (gated: `decision_latency` module).
 *
 * "We're slow at decisions" decomposed into named stages with numbers. Lowe's
 * observation is the demo this page exists to render: analysis takes thirty
 * minutes and decisions still take weeks, so the interesting number is never
 * the total — it is which segment ate the time.
 *
 * READ-ONLY. No writes, no ceremonies, no actions. The page computes nothing
 * itself: arithmetic lives in `decisionLatencyCore.ts`, fetching in
 * `decisionLatencyService.ts`.
 *
 * THE DESIGN CONSTRAINT THAT SHAPES EVERYTHING HERE: an unmeasured stage must
 * never look like a fast one. A bar chart's natural behaviour is to draw a
 * missing value as a short bar, which would read as "authorization is quick"
 * when the truth is "we cannot see authorization at all". So unmeasured stages
 * render as a hatched gap carrying the name of the missing stamp, and the
 * summary states the measurable ratio before it states any duration. This
 * report's whole commercial value is surviving an executive's "prove we're slow
 * at approval, not deliberation" — a report that quietly renders gaps as speed
 * would lose that argument the first time it was checked.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Timer, RefreshCw, AlertCircle } from 'lucide-react';
import { Panel, DataTable, Callout, EmptyState, Skeleton, Select, type Column } from '../../components/ds';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { fetchLatencyReport } from '../../lib/decisionLatencyService';
import {
  STAGE_LABELS,
  STAGE_SPANS,
  formatDuration,
  narrateAggregate,
  type LatencyChain,
  type StageAggregate,
} from '../../lib/decisionLatencyCore';
import type { LatencyReport } from '../../lib/decisionLatencyService';
import { DecisionExecutionTrend } from './DecisionExecutionTrend';

const RANGES = [
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: '180d', label: 'Last 180 days', days: 180 },
  { key: 'all', label: 'All time', days: null },
] as const;

type RangeKey = (typeof RANGES)[number]['key'];

function sinceFor(range: RangeKey): string | undefined {
  const entry = RANGES.find((r) => r.key === range);
  if (!entry?.days) return undefined;
  return new Date(Date.now() - entry.days * 24 * 3600_000).toISOString();
}

/**
 * One stage's row in the decomposition.
 *
 * The bar is scaled against the widest measured p90 on the page, so stages are
 * comparable to each other rather than to an absolute scale — the question this
 * report answers is "which segment dominates", not "how many days is a lot".
 */
function StageBar({ stage, widestP90 }: { stage: StageAggregate; widestP90: number }) {
  const measured = stage.measuredChains > 0 && stage.p90Ms !== null;

  if (!measured) {
    return (
      <div className="flex items-center gap-3 py-2">
        <div className="w-40 shrink-0 text-sm text-[var(--text-2)]">{STAGE_LABELS[stage.stage]}</div>
        <div
          className="h-5 flex-1 rounded-sm border border-dashed border-[var(--line-2)] bg-[repeating-linear-gradient(45deg,var(--surface-2)_0px,var(--surface-2)_6px,transparent_6px,transparent_12px)]"
          aria-label={`${STAGE_LABELS[stage.stage]}: unmeasured`}
        />
        <div className="w-32 shrink-0 text-right font-mono text-xs uppercase tracking-wide text-[var(--text-3)]">
          unmeasured
        </div>
      </div>
    );
  }

  const pct = widestP90 > 0 ? Math.max(2, ((stage.p90Ms ?? 0) / widestP90) * 100) : 2;

  return (
    <div className="flex items-center gap-3 py-2">
      <div className="w-40 shrink-0 text-sm text-[var(--text-1)]">{STAGE_LABELS[stage.stage]}</div>
      <div className="h-5 flex-1 rounded-sm bg-[var(--surface-2)]">
        <div
          className="h-full rounded-sm bg-[var(--slate-blue,#4a5d7e)]"
          style={{ width: `${pct}%` }}
          aria-label={`${STAGE_LABELS[stage.stage]}: p90 ${formatDuration(stage.p90Ms)}`}
        />
      </div>
      <div className="w-32 shrink-0 text-right font-mono text-xs tabular-nums text-[var(--text-1)]">
        {formatDuration(stage.p90Ms)}
      </div>
    </div>
  );
}

export default function DecisionLatencyReportPage() {
  const [range, setRange] = useState<RangeKey>('180d');
  const [report, setReport] = useState<LatencyReport | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) {
      setReport({ chains: [], aggregate: { chains: 0, completeChains: 0, stages: [], measurableRatio: 0, p90OffenderStage: null }, error: 'No workspace selected' });
      setLoading(false);
      return;
    }
    setLoading(true);
    const next = await fetchLatencyReport(workspaceId, { since: sinceFor(range) });
    setReport(next);
    setLoading(false);
  }, [range]);

  useEffect(() => {
    void load();
  }, [load]);

  // Computed once per range: a fresh Date.now() each render would refetch the trend forever.
  const trendSince = useMemo(() => sinceFor(range), [range]);

  const widestP90 = useMemo(() => {
    if (!report) return 0;
    return report.aggregate.stages.reduce((max, s) => Math.max(max, s.p90Ms ?? 0), 0);
  }, [report]);

  const chainColumns: Column<LatencyChain>[] = useMemo(
    () => [
      {
        key: 'label',
        header: 'Chain',
        render: (row) => (
          <span className="font-mono text-xs text-[var(--text-2)]">{row.label ?? row.id.slice(0, 8)}</span>
        ),
      },
      {
        key: 'dominant',
        header: 'Dominant stage',
        render: (row) =>
          row.dominantStage ? (
            <span className="text-sm">{STAGE_LABELS[row.dominantStage]}</span>
          ) : (
            <span className="text-sm text-[var(--text-3)]">nothing measurable</span>
          ),
      },
      {
        key: 'measuredTotalMs',
        header: 'Measured total',
        numeric: true,
        render: (row) => formatDuration(row.measuredTotalMs || null),
      },
      {
        key: 'coverage',
        header: 'Stages measured',
        numeric: true,
        render: (row) => (
          <span className={row.complete ? '' : 'text-[var(--text-3)]'}>
            {row.measuredCount}/7
            {row.anomalousCount > 0 ? ` · ${row.anomalousCount} anomalous` : ''}
          </span>
        ),
      },
    ],
    [],
  );

  if (loading && !report) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const agg = report?.aggregate;

  return (
    <div className="space-y-6 p-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-wide text-[var(--text-3)]">
            <Timer className="h-3.5 w-3.5" />
            Decision latency
          </div>
          <h1 className="mt-1 text-2xl text-[var(--text-1)]">Where the time actually goes</h1>
          <p className="mt-1 max-w-2xl text-sm text-[var(--text-2)]">
            Every stage below is a subtraction between two timestamps the record already carried.
            Nothing here is estimated, and a stage we cannot measure is shown as a gap rather than
            as a fast one.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Select
            size="sm"
            value={range}
            onChange={(e) => setRange(e.target.value as RangeKey)}
            aria-label="Date range"
            options={RANGES.map((r) => ({ value: r.key, label: r.label }))}
          />
          <button
            type="button"
            onClick={() => void load()}
            className="flex items-center gap-1.5 border border-[var(--line-2)] px-2 py-1 text-sm text-[var(--text-2)] hover:text-[var(--text-1)]"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </header>

      {report?.error && (
        <Callout tone="coral" label="Could not load" title="The report is incomplete">
          {report.error}
        </Callout>
      )}

      {agg && agg.chains === 0 && !report?.error && (
        <EmptyState
          icon={Timer}
          size="page"
          title="No decision chains in this range"
          description="A chain begins when a catchball loop issues its ask. Widen the range, or run a cycle end to end."
          action={
            <button
              type="button"
              onClick={() => setRange('all')}
              className="border border-[var(--line-2)] px-3 py-1.5 text-sm"
            >
              Show all time
            </button>
          }
        />
      )}

      {agg && agg.chains > 0 && (
        <>
          <Callout
            tone={agg.measurableRatio < 0.5 ? 'mustard' : 'slate'}
            label="Reading"
            title={narrateAggregate(agg)}
          >
            <span className="text-sm">
              {agg.chains} chain{agg.chains === 1 ? '' : 's'} · {agg.completeChains} fully
              measurable · {Math.round(agg.measurableRatio * 100)}% of stage segments measured.
              {agg.measurableRatio < 1 && (
                <>
                  {' '}
                  Unmeasured segments are missing timestamps, not zero durations — the stages they
                  belong to are absent from this reading rather than fast.
                </>
              )}
            </span>
          </Callout>

          <Panel label="Decomposition" title="p90 by stage">
            <div className="divide-y divide-[var(--line-1)]">
              {agg.stages.map((stage) => (
                <StageBar key={stage.stage} stage={stage} widestP90={widestP90} />
              ))}
            </div>
            <dl className="mt-4 grid gap-x-6 gap-y-1 border-t border-[var(--line-1)] pt-3 text-xs text-[var(--text-3)] sm:grid-cols-2">
              {agg.stages.map((stage) => (
                <div key={stage.stage} className="flex justify-between gap-2">
                  <dt>{STAGE_LABELS[stage.stage]}</dt>
                  <dd className="text-right">
                    {STAGE_SPANS[stage.stage]}
                    {stage.measuredChains > 0
                      ? ` · median ${formatDuration(stage.medianMs)} over ${stage.measuredChains}`
                      : ' · no measurable chain'}
                  </dd>
                </div>
              ))}
            </dl>
          </Panel>

          {agg.stages.some((s) => s.anomalousChains > 0) && (
            <Callout tone="mustard" label="Data quality" title="Out-of-order timestamps found">
              <span className="text-sm">
                {agg.stages
                  .filter((s) => s.anomalousChains > 0)
                  .map((s) => `${STAGE_LABELS[s.stage]} (${s.anomalousChains})`)
                  .join(', ')}
                {' '}— these segments end before they start and are excluded from every number on
                this page rather than averaged in as negative durations.
              </span>
            </Callout>
          )}

          <Panel label="Chains" title="Per-loop drill-in" count={report?.chains.length} flush>
            <DataTable
              columns={chainColumns}
              rows={report?.chains ?? []}
              getRowKey={(row) => row.id}
              density="compact"
              rule="horizontal"
              zebra
            />
          </Panel>

          <Callout tone="neutral" label="Known gap" title="Authorization is not yet measurable">
            <span className="flex items-start gap-2 text-sm">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                The ratified-report → Plan-of-Record segment needs{' '}
                <code className="font-mono text-xs">source_cycle_report_id</code>, which migration
                678 deliberately left NULL rather than guess. Until the propagation PRD&rsquo;s P1
                wires it, that stage reports as unmeasured on every chain by construction.
              </span>
            </span>
          </Callout>
        </>
      )}

      <DecisionExecutionTrend workspaceId={getCurrentWorkspaceId()} since={trendSince} />
    </div>
  );
}
